
import React, { useState, useRef, useEffect, useImperativeHandle, useMemo } from 'react'
import { Property } from 'csstype'
import styled, { css } from 'styled-components'
import cn from 'clsx'
import { getImageUrl } from 'lib/imageUtils'
import config from 'constants/config'
import LoadingBox from '../Loading/LoadingBox'

interface Picture extends React.HTMLAttributes<HTMLPictureElement> {
  $position?: Property.ObjectPosition<string | 0>;
}

const StyledPicture = styled.picture<Picture>`
  position: relative;
  z-index: 0;

  ${({ $position }) => $position && css`
    height: 100%;
    width: 100%;
    overflow: hidden;

    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
      object-position: ${$position};
    }
  `}
`

const ImgElement = styled.img`
  opacity: 1;
  transition: opacity 0.2s;
  position: relative;

  &.hide {
    opacity: 0;
  }
`

const PlaceholderImage = styled.div`
  background-size: cover;
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(8px);
`

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  image?: App.Image;
  id?: string;
  aspectRatio?: string;
  className?: string;
  type?: string;
  dpr?: 1 | 2 | 3;
  highRes?: boolean;
  format?: 'jpeg' | 'jpg' | 'png' | 'webp' | 'svg' | 'avif';
  enhancedFormats?: boolean;
  width?: number | string;
  height?: number | string;
  gravity?: 'auto' | 'center' | 'east' | 'north' | 'northeast' | 'northwest' | 'south' | 'southeast' | 'southwest' | 'west' // auto is centre
  quality?: 'best' | 'good' | 'eco' | 'low';
  fit?: Property.ObjectPosition<string>;
  withPlaceholder?: boolean;
}

export interface ImageProps extends Props {}

const Image = React.forwardRef<HTMLImageElement, Props>((props: Props, ref) => {
  const {
    id: propsId,
    aspectRatio,
    dpr,
    type,
    highRes,
    format,
    enhancedFormats = true,
    quality,
    gravity,
    alt,
    fit,
    className,
    onLoad,
    image,
    withPlaceholder,
    ...imageProps
  } = props

  const shouldLazyLoad = props.loading === 'lazy'
  const [showImage, setShowImage] = useState(!shouldLazyLoad)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  useImperativeHandle(ref, () => imgRef.current!)
  useEffect(() => {
    if (showImage && imgRef.current && imgRef.current.complete && !loaded) {
      setLoaded(true)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (shouldLazyLoad && !showImage) {
      const imageElement = imgRef.current
      const observer = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          setShowImage(true)
        }
      }, { rootMargin: '200px 0px 200px 0px' })

      if (imageElement) {
        observer.observe(imageElement)
      }

      return () => observer?.disconnect()
    }
  }, [shouldLazyLoad, showImage])

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true)
    onLoad?.(e)
  }

  // allow passing of an ID or of an image object
  const id = image?.id ?? propsId
  // always prefer ID over URL
  const externalUrl = id ? undefined : image?.url

  let imageUrl, webpUrl, avifUrl, placeholderUrl
  if (externalUrl) {
    imageUrl = externalUrl
  } else if (id) {
    imageUrl = getImageUrl(id, {
      width: props.width,
      height: props.height,
      quality,
      aspectRatio,
      type,
      highRes,
      format,
      gravity,
      dpr,
    })

    webpUrl = getImageUrl(id, {
      width: props.width,
      height: props.height,
      quality,
      aspectRatio,
      type,
      highRes,
      format: 'webp',
      gravity,
      dpr,
    })

    avifUrl = getImageUrl(id, {
      width: props.width,
      height: props.height,
      quality,
      aspectRatio,
      type,
      highRes,
      format: 'avif',
      gravity,
      dpr,
    })

    placeholderUrl = getImageUrl(id, {
      width: 64,
      aspectRatio,
      type,
    })
  }

  const showPlaceholder = withPlaceholder && !loaded
  const showLoader = (!showImage || !loaded) && !withPlaceholder

  const placeholderImage = useMemo(() => ({
    backgroundImage: placeholderUrl,
    backgroundPosition: fit,
  }), [fit, placeholderUrl])

  return (
    <StyledPicture className={cn({ fit: !!fit })} $position={fit}>
      {showPlaceholder && <PlaceholderImage style={placeholderImage} />}
      {config.AVIF_ENABLED && showImage && avifUrl && enhancedFormats &&
        <source
          type="image/avif"
          srcSet={avifUrl}
        />
      }
      {showImage && enhancedFormats && webpUrl &&
        <source
          type="image/webp"
          srcSet={webpUrl}
        />
      }
      <ImgElement
        {...imageProps}
        onLoad={onImageLoad}
        loading="eager"
        className={cn(className, { hide: !showImage })}
        ref={imgRef}
        alt={alt ?? image?.title}
        src={showImage ? imageUrl : ''}
        />
      {showLoader && <LoadingBox floating />}
    </StyledPicture>
  )
})

Image.displayName = 'Image'

export default React.memo(Image)
